import React, { memo, useMemo } from "react";
import { LogTimestampProps } from "../types";
import { useAppSelector } from "../../../store/hooks";
import { selectValue as selectTimezone } from "../../../store/timezoneSlice";
import "./LogTimestamp.css";

/**
 * Format timestamp as "YYYY-MM-DD HH:mm:ss.SSS" in the specified timezone
 */
function formatTimestamp(timestamp: number, timezone: string): string {
  const date = new Date(timestamp);

  // Use Intl.DateTimeFormat for timezone-aware formatting
  const formatter = new Intl.DateTimeFormat("en", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  const parts = formatter.formatToParts(date);
  const getPart = (type: string) => parts.find((p) => p.type === type)?.value || "";

  const year = getPart("year");
  const month = getPart("month");
  const day = getPart("day");
  const hour = getPart("hour");
  const minute = getPart("minute");
  const second = getPart("second");

  // Get milliseconds (not available in Intl, calculate manually)
  const ms = String(date.getMilliseconds()).padStart(3, "0");

  return `${year}-${month}-${day} ${hour}:${minute}:${second}.${ms}`;
}

/**
 * Displays timestamp in readable format based on timezone setting
 */
export const LogTimestamp = memo(function LogTimestamp({ timestamp }: LogTimestampProps) {
  const timezone = useAppSelector(selectTimezone);

  const formattedTime = useMemo(() => {
    return formatTimestamp(timestamp, timezone);
  }, [timestamp, timezone]);

  // ISO string for tooltip (always UTC for consistency)
  const isoTime = useMemo(() => {
    return new Date(timestamp).toISOString();
  }, [timestamp]);

  return (
    <div className="log-timestamp" title={isoTime}>
      {formattedTime}
    </div>
  );
});
